import { attr, customElement, FASTElement, observable } from '@microsoft/fast-element';
import { SummaryChatTemplate as template } from './summary-chat.template';
import { SummaryChatStyles as styles } from './summary-chat.styles';
import { CASCADE_ENUM, EnumTranslate, IEnumService } from '../../../../services/enum.service';
import {
  formatMMDDYYYY,
  getEnumReadableValue,
  getFormattedNumber,
} from '../../../../utils/formatting';
import { INegotiationService } from '../../../../services';
import { INegotiationSummary, IRfq, IVppa } from '../../../../types';
import { StructuredChatEnums } from '../structured-chat';
import {
  IStructuredChatService,
  VALID_STRUCTURED_CHAT_STATUS,
} from '../../../../services/structured-chat.service';
import { CONTRACT_TYPE, PROJECT_OPERATIONAL_STATUS } from '../../../../utils';
import { getAbbreviationForCreditProfile } from '../../../../utils/credit-profiles'

const name = 'summary-chat';

@customElement({
  name,
  template,
  styles,
})
export class SummaryChat extends FASTElement {
  @IEnumService enumService: IEnumService;
  @INegotiationService negotiationService: INegotiationService;
  @IStructuredChatService structuredChatService: IStructuredChatService;

  public summaryTableBody!: HTMLElement;

  @attr negotiationId: string = '';
  @attr structuredChatEnums: StructuredChatEnums;
  @attr vppaRfqData: { vppaData: IVppa; rfqData: IRfq };
  @attr rfqCompany: string;
  @attr newBuilds: String[] = [CONTRACT_TYPE.NEW_BUILD_VPPA, CONTRACT_TYPE.NEW_BUILD_PPA];

  @observable summary: INegotiationSummary = {} as INegotiationSummary;
  @observable negativePricingMethod: Array<EnumTranslate>;
  @observable applicableRECProgram: Array<EnumTranslate>;
  @observable applicableTrackingSystem: Array<EnumTranslate>;
  @observable recDeliveryMethodology: Array<EnumTranslate>;
  @observable alternativeSwapSettlementCalculationMethod: Array<EnumTranslate>;
  @observable performanceGuaranteeMethod: Array<EnumTranslate>;
  @observable technologyTypes: Array<EnumTranslate>;
  @observable contractTypes: Array<EnumTranslate>;
  @observable companyCounterpartyMap: Map<string, string> = new Map([
    ["Seller", "Required to be Seller"],
    ["Buyer", "Required to be Buyer"],
    ["Counterparties may decide", "Equal reporting status, counterparties may decide"]
  ]);
  @observable productType: string = 'Contract';
  @observable summaryDescription: string = '';

  connectedCallback(): void {
    super.connectedCallback();
    this.loadData();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.summary = {} as INegotiationSummary;
    this.summaryTableBody.innerHTML = '';
  }

  private async loadData() {
    // TODO -> TRANSFER THAT LOADENUMS TO ITS PARENT STRUCTURED-CHAT COMPONENT FOR BETTER PERFORMANCE
    await this.loadEnums();
    if (this.negotiationId) {
      const neg = this.structuredChatService.combinedNegotiations.filter(
        (negotiation) => negotiation.NEGOTIATION_ID === this.negotiationId,
      );
      if (neg.length && neg[0].NEGOTIATION_STATUS !== VALID_STRUCTURED_CHAT_STATUS.STAGED) {
        this.getNegotiationConfirmation();
      } else if (neg[0].NEGOTIATION_STATUS === VALID_STRUCTURED_CHAT_STATUS.STAGED) {
        this.getStagedNegotiationInformation();
      }
    } else if (this.vppaRfqData.rfqData && this.vppaRfqData.vppaData) {
      this.getRfqVppaComparisonInformation();
    }
  }

  getSummaryDescription() {
    if (this.summary.VPPA_CONTRACT_TYPE === CONTRACT_TYPE.REC) {
      this.summaryDescription =
        'This contract description incorporates by reference the Standalone REC Summary, which can be further negotiated and amended by the parties.';
    } else {
      this.summaryDescription =
      `This contract description incorporates by reference the ${getEnumReadableValue(this.contractTypes, this.summary.VPPA_CONTRACT_TYPE)} published on the website of the SEF.`;
    }
  }

  getRfqVppaComparisonInformation() {
    if (this.vppaRfqData.vppaData.CONTRACT_TYPE === CONTRACT_TYPE.REC) {
      this.productType = CONTRACT_TYPE.REC;
    }
    if (this.vppaRfqData.rfqData && this.vppaRfqData.vppaData) {
      const { vppaData, rfqData } = this.vppaRfqData;
      this.summary = {
        VPPA_ID: vppaData.VPPA_ID,
        VPPA_UNIQUE_ID: vppaData.UNIQUE_ID,
        RFQ_UNIQUE_ID: rfqData.UNIQUE_ID,
        BUYER_COMPANY_NAME: this.rfqCompany,
        SELLER_COMPANY_NAME: vppaData.COMPANY_NAME,
        NEGOTIATION_ID: null,
        CONTRACT_SYMBOL_VPPA: vppaData.TICKER,
        CONTRACT_SYMBOL_RFQ: rfqData.TICKER,
        BUYER: rfqData.CASCADE_COMPANY_ID,
        SELLER: vppaData.CASCADE_COMPANY_ID,
        NAME: vppaData.PROJECT_NAME,
        VPPA_TICKER: vppaData.TICKER,
        RFQ_TICKER: rfqData.TICKER,
        VPPA_TECHNOLOGY: vppaData.TECHNOLOGY,
        RFQ_TECHNOLOGY: rfqData.TECHNOLOGY,
        VPPA_TARGET_COD: vppaData.TARGET_COD,
        VPPA_COD: vppaData.COD,
        RFQ_TARGET_COD: rfqData.TARGET_COD,
        VPPA_GUARANTEED_COD: vppaData.GUARANTEED_COD,
        RFQ_GUARANTEED_COD: rfqData.GUARANTEED_COD,
        MEGAWATT_AC: vppaData.MEGAWATT_AC,
        OFFERED_CAPACITY: vppaData.OFFERED_CAPACITY,
        BUYERS_FRACTION: vppaData.BUYERS_FRACTION,
        VPPA_TENOR_START_DATE: vppaData.TENOR_START_DATE,
        RFQ_TENOR_START_DATE: rfqData.TENOR_START_DATE,
        VPPA_TENOR_MONTHS: vppaData.TENOR_MONTHS,
        RFQ_TENOR_MONTHS: rfqData.TENOR_MONTHS,
        VPPA_FIXED_PRICE: vppaData.FIXED_PRICE,
        RFQ_FIXED_PRICE: rfqData.FIXED_PRICE,
        VPPA_ISO: vppaData.ISO,
        RFQ_ISO: rfqData.ISO,
        VPPA_SETTLEMENT_POINT:
          vppaData.CONTRACT_TYPE === CONTRACT_TYPE.REC
            ? CONTRACT_TYPE.REC
            : vppaData.SETTLEMENT_POINT,
        RFQ_SETTLEMENT_POINT:
          vppaData.CONTRACT_TYPE === CONTRACT_TYPE.REC
            ? CONTRACT_TYPE.REC
            : rfqData.SETTLEMENT_POINT,
        VPPA_SETTLEMENT_INDEX:
          vppaData.CONTRACT_TYPE === CONTRACT_TYPE.REC
            ? CONTRACT_TYPE.REC
            : vppaData.SETTLEMENT_INDEX,
        RFQ_SETTLEMENT_INDEX:
          vppaData.CONTRACT_TYPE === CONTRACT_TYPE.REC
            ? CONTRACT_TYPE.REC
            : rfqData.SETTLEMENT_INDEX_STRING,
        INTERCONNECTION_POINT:
          vppaData.CONTRACT_TYPE === CONTRACT_TYPE.REC
            ? CONTRACT_TYPE.REC
            : vppaData.INTERCONNECTION_POINT,
        VPPA_NEGATIVE_PRICING_METHOD:
          vppaData.CONTRACT_TYPE === CONTRACT_TYPE.REC
            ? CONTRACT_TYPE.REC
            : vppaData.NEGATIVE_PRICING_METHOD,
        RFQ_NEGATIVE_PRICING_METHOD:
          vppaData.CONTRACT_TYPE === CONTRACT_TYPE.REC
            ? CONTRACT_TYPE.REC
            : rfqData.RFQ_NEGATIVE_PRICING_METHOD,
        PROJECT_OPERATIONAL_STATUS: vppaData.PROJECT_OPERATIONAL_STATUS,
        RFQ_CREDIT_PROFILE: rfqData.RFQ_CREDIT_PROFILE,
        SELLER_POST_COD_COLLATERAL: vppaData.SELLER_POST_COD_COLLATERAL,
        BUYER_POST_COD_COLLATERAL: rfqData.BUYER_POST_COD_COLLATERAL,
        RFQ_APPLICABLE_REC_PROGRAM: rfqData.RFQ_APPLICABLE_REC_PROGRAM,
        VPPA_APPLICABLE_REC_PROGRAM: vppaData.APPLICABLE_REC_PROGRAM,
        RFQ_APPLICABLE_TRACKING_SYSTEM: rfqData.APPLICABLE_TRACKING_SYSTEM,
        VPPA_APPLICABLE_TRACKING_SYSTEM: vppaData.APPLICABLE_TRACKING_SYSTEM,
        RFQ_REC_DELIVERY_METHODOLOGY: rfqData.RFQ_REC_DELIVERY_METHODOLOGY,
        VPPA_REC_DELIVERY_METHODOLOGY: vppaData.REC_DELIVERY_METHODOLOGY,
        VPPA_PERFORMANCE_GUARANTEE_METHOD: vppaData.PERFORMANCE_GUARANTEE_METHOD,
        RFQ_PERFORMANCE_GUARANTEE_METHOD: rfqData.RFQ_PERFORMANCE_GUARANTEE_METHOD,
        VPPA_CONTRACT_TYPE: vppaData.CONTRACT_TYPE,
        RFQ_CONTRACT_TYPE: rfqData.CONTRACT_TYPE,
        VPPA_CREDIT_PROFILE: vppaData.VPPA_CREDIT_PROFILE
      };
    }
    this.renderRows();
  }

  getNegotiationConfirmation() {
    this.negotiationService
      .getNegotiationConfirmation(this.negotiationId)
      .then((response) => {
        if (response?.REPLY[0]) {
          this.summary = response.REPLY[0] as INegotiationSummary;
          if (
            this.summary.VPPA_CONTRACT_TYPE === CONTRACT_TYPE.REC &&
            this.summary.RFQ_CONTRACT_TYPE === CONTRACT_TYPE.REC
          ) {
            this.summary.VPPA_SETTLEMENT_POINT = CONTRACT_TYPE.REC;
            this.summary.VPPA_SETTLEMENT_INDEX = CONTRACT_TYPE.REC;
            this.summary.INTERCONNECTION_POINT = CONTRACT_TYPE.REC;
            this.summary.RFQ_SETTLEMENT_INDEX = CONTRACT_TYPE.REC;
            this.summary.RFQ_SETTLEMENT_POINT = CONTRACT_TYPE.REC;
            this.summary.VPPA_NEGATIVE_PRICING_METHOD = CONTRACT_TYPE.REC;
            this.summary.RFQ_NEGATIVE_PRICING_METHOD = CONTRACT_TYPE.REC;
          }
          this.getSummaryDescription();
        } else {
          this.summary = {} as INegotiationSummary;
        }
      })
      .catch((error) => {
        this.summary = {} as INegotiationSummary;
        // TODO -> Send a error message to the toast
      })
      .finally(() => {
        this.renderRows();
      });
  }

  getStagedNegotiationInformation() {
    this.summary = this.structuredChatService.allStagedNegotiations.filter(
      (neg) => neg.NEGOTIATION_ID === this.negotiationId,
    )[0] as unknown as INegotiationSummary;
    this.renderRows();
  }

  private async loadEnums() {
    this.applicableRECProgram = this.structuredChatEnums.applicableRECProgram;
    this.applicableTrackingSystem = this.structuredChatEnums.applicableTrackingSystem;
    this.recDeliveryMethodology = this.structuredChatEnums.recDeliveryMethodology;

    this.alternativeSwapSettlementCalculationMethod =
      this.structuredChatEnums.alternativeSwapSettlementCalculationMethod;

    this.technologyTypes = this.structuredChatEnums.technologyTypes;

    this.negativePricingMethod = this.structuredChatEnums.negativePricingMethod;

    this.performanceGuaranteeMethod = this.structuredChatEnums.performanceGuaranteeMethod;
    this.contractTypes = this.structuredChatEnums.contractTypes;
  }

  renderRows() {
    const { summaryTableBody, rows, summary } = this;
    if (!summaryTableBody) return;

    rows.forEach((r) => {
      if (
        ((summary.VPPA_SETTLEMENT_POINT === CONTRACT_TYPE.REC ||
          summary.RFQ_SETTLEMENT_POINT === CONTRACT_TYPE.REC) &&
          r.label === 'Floating Price Settlement Point') ||
        ((summary.VPPA_SETTLEMENT_INDEX === CONTRACT_TYPE.REC ||
          summary.RFQ_SETTLEMENT_INDEX === CONTRACT_TYPE.REC) &&
          r.label === 'Floating Price Settlement Point Index') ||
        (summary.INTERCONNECTION_POINT === CONTRACT_TYPE.REC &&
          r.label === 'Interconnection Point') ||
        ((summary.VPPA_NEGATIVE_PRICING_METHOD === CONTRACT_TYPE.REC ||
          summary.RFQ_NEGATIVE_PRICING_METHOD === CONTRACT_TYPE.REC) &&
          r.label === 'Negative Pricing Method') ||
        ((this.productType === CONTRACT_TYPE.REC) &&
          (r.label === 'Target COD' || r.label === 'Guaranteed COD'))
      ) {
        return;
      }
      if (
        (summary.PROJECT_OPERATIONAL_STATUS === PROJECT_OPERATIONAL_STATUS.NEW_BUILD &&
          r.label === 'COD') ||
        (summary.PROJECT_OPERATIONAL_STATUS === PROJECT_OPERATIONAL_STATUS.OPERATIONAL &&
          (r.label === 'Target COD' || r.label === 'Guaranteed COD')) ||
        (!(this.newBuilds.includes(summary.VPPA_CONTRACT_TYPE)) &&
          (r.label === 'Target COD' || r.label === 'Guaranteed COD'))
      ) {
        return;
      }

      if (
          summary.VPPA_CONTRACT_TYPE !== CONTRACT_TYPE.NEW_BUILD_VPPA &&
          summary.VPPA_CONTRACT_TYPE !== CONTRACT_TYPE.POST_BUILD_VPPA &&
          (r.label === 'Unique Transaction Identifier (UTI)' ||
           r.label === 'Unique Product Identifier (UPI)' ||
           r.label === 'Reporting Counterparty')
      ) {
        return;
      }

      const tr = document.createElement('tr');
      const th = document.createElement('th');
      const tda = document.createElement('td');
      const tdb = document.createElement('td');

      th.innerHTML = r.label;
      tr.appendChild(th);

      if (r.hasOwnProperty('valueA')) {
        tda.innerHTML = summary.hasOwnProperty(r.valueA) ? summary[r.valueA] : '';

        // format the value if the object has a format function
        if (r.hasOwnProperty('format')) tda.innerHTML = r.format(summary[r.valueA]);

        if (
          !r.hasOwnProperty('valueB') ||
          (r.hasOwnProperty('valueB') && summary[r.valueA] === summary[r.valueB] && r.compare)
        ) {
          tda.setAttribute('colspan', '2');
        } else if (r.valueB && summary[r.valueA] !== summary[r.valueB] && r.compare) {
          tda.classList.add('highlighted');
        }

        tr.appendChild(tda);
      }

      if (r.hasOwnProperty('valueB')) {
        tdb.innerHTML = summary.hasOwnProperty(r.valueB) ? summary[r.valueB] : '';

        // format the value if the object has a format function
        if (r.hasOwnProperty('format')) tdb.innerHTML = r.format(summary[r.valueB]);

        if (summary[r.valueA] !== summary[r.valueB] && r.compare) {
          tdb.classList.add('highlighted');
          tr.appendChild(tdb);
        } else if (!r.compare) {
          tr.appendChild(tdb);
        }
      }

      summaryTableBody.appendChild(tr);
    });
  }

  rows = [
    {
      label: 'Unique Product Identifier (UPI)',
      valueA: 'UPI',
    },
    {
      label: 'Unique ID',
      valueA: 'VPPA_UNIQUE_ID',
      valueB: 'RFQ_UNIQUE_ID',
    },
    {
      label: 'Contract Symbol',
      valueA: 'VPPA_TICKER',
      valueB: 'RFQ_TICKER',
    },
    {
      label: 'Contract Type',
      valueA: 'VPPA_CONTRACT_TYPE',
      valueB: 'RFQ_CONTRACT_TYPE',
      format: (value) => {
        return getEnumReadableValue(this.contractTypes, value);
      },
    },
    {
      label: 'Buyer',
      valueA: 'BUYER_COMPANY_NAME',
    },
    {
      label: 'Seller',
      valueA: 'SELLER_COMPANY_NAME',
    },
    {
      label: 'Project Name',
      valueA: 'NAME',
    },
    {
      label: 'Reporting Counterparty',
      valueA: 'REPORTING_COUNTERPARTY',
      format: (value) => {
        if (value !== null) {
          return this.companyCounterpartyMap.get(value)
        } else {
          return "Calculated on counterparty accepting/sending proposal"
        }
      }
    },
    {
      label: 'Technology Type',
      valueA: 'VPPA_TECHNOLOGY',
      valueB: 'RFQ_TECHNOLOGY',
      compare: true,
      format: (value) => {
        return getEnumReadableValue(this.technologyTypes, value);
      },
    },
    {
      label: 'Target COD',
      valueA: 'VPPA_TARGET_COD',
      valueB: 'RFQ_TARGET_COD',
      compare: true,
      format: (value) => {
        return formatMMDDYYYY(value);
      },
    },
    {
      label: 'Guaranteed COD',
      valueA: 'VPPA_GUARANTEED_COD',
      valueB: 'RFQ_GUARANTEED_COD',
      compare: true,
      format: (value) => {
        return formatMMDDYYYY(value);
      },
    },
    {
      label: 'COD',
      valueA: 'VPPA_COD',
      format: (value) => {
        return formatMMDDYYYY(value);
      },
    },
    {
      label: 'Nameplate Capacity',
      valueA: 'MEGAWATT_AC',
      compare: true,
      format: (value) => (value ? `${getFormattedNumber(value)}MW` : ''),
    },
    {
      label: 'Offered Capacity',
      valueA: 'OFFERED_CAPACITY',
      compare: true,
      format: (value) => (value ? `${getFormattedNumber(value)}MW` : ''),
    },
    {
      label: "Buyer's Fraction",
      valueA: 'BUYERS_FRACTION',
      compare: true,
      format: (value) => {
        return value ? `${getFormattedNumber(value)}%` : '';
      },
    },
    {
      label: 'Tenor Start Date',
      valueA: 'VPPA_TENOR_START_DATE',
      valueB: 'RFQ_TENOR_START_DATE',
      compare: true,
      format: (value) => {
        return formatMMDDYYYY(value);
      },
    },
    {
      label: 'Tenor (months)',
      valueA: 'VPPA_TENOR_MONTHS',
      valueB: 'RFQ_TENOR_MONTHS',
      compare: true,
    },
    {
      label: 'Fixed Price',
      valueA: 'VPPA_FIXED_PRICE',
      valueB: 'RFQ_FIXED_PRICE',
      compare: true,
      format: (value) => (value ? `$${value}/MWh` : ''),
    },
    {
      label: 'ISO',
      valueA: 'VPPA_ISO',
      valueB: 'RFQ_ISO',
      compare: true,
      // TODO -> format its value
    },
    {
      label: 'Floating Price Settlement Point',
      valueA: 'VPPA_SETTLEMENT_POINT',
      valueB: 'RFQ_SETTLEMENT_POINT',
      compare: true,
    },
    {
      label: 'Floating Price Settlement Point Index',
      valueA: 'VPPA_SETTLEMENT_INDEX',
      valueB: 'RFQ_SETTLEMENT_INDEX',
      compare: true,
      format: (value) => {
        return this.enumService.getEnumTranslation(CASCADE_ENUM.SETTLEMENT_INDEX, value);
      },
    },
    {
      label: 'Interconnection Point',
      valueA: 'INTERCONNECTION_POINT',
    },
    {
      label: 'Negative Pricing Method',
      valueA: 'VPPA_NEGATIVE_PRICING_METHOD',
      valueB: 'RFQ_NEGATIVE_PRICING_METHOD',
      compare: true,
      format: (value) => {
        return getEnumReadableValue(this.negativePricingMethod, value);
      },
    },
    {
      label: ' Buyer Post-COD Credit $/MW (Max)',
      valueA: 'BUYER_POST_COD_COLLATERAL',
      format: (value) => (value ? `$${getFormattedNumber(value)}/MW` : getAbbreviationForCreditProfile(this.summary.RFQ_CREDIT_PROFILE)),
    },
    {
      label: ' Seller Post-COD Credit $/MW (Max)',
      valueA: 'SELLER_POST_COD_COLLATERAL',
      format: (value) => (value ? `$${getFormattedNumber(value)}/MW` : getAbbreviationForCreditProfile(this.summary.VPPA_CREDIT_PROFILE)),
    },
    {
      label: 'Applicable REC Program',
      valueA: 'VPPA_APPLICABLE_REC_PROGRAM',
      valueB: 'RFQ_APPLICABLE_REC_PROGRAM',
      compare: true,
      format: (value) => {
        return getEnumReadableValue(this.applicableRECProgram, value);
      },
    },
    {
      label: 'Applicable Tracking System',
      valueA: 'VPPA_APPLICABLE_TRACKING_SYSTEM',
      valueB: 'RFQ_APPLICABLE_TRACKING_SYSTEM',
      compare: true,
      format: (value) => {
        return getEnumReadableValue(this.applicableTrackingSystem, value);
      },
    },
    {
      label: 'REC Delivery Methodology',
      valueA: 'VPPA_REC_DELIVERY_METHODOLOGY',
      valueB: 'RFQ_REC_DELIVERY_METHODOLOGY',
      compare: true,
      format: (value) => {
        return getEnumReadableValue(this.recDeliveryMethodology, value);
      },
    },
    {
      label: 'Performance Guarantee Method',
      valueA: 'VPPA_PERFORMANCE_GUARANTEE_METHOD',
      valueB: 'RFQ_PERFORMANCE_GUARANTEE_METHOD',
      compare: true,
      format: (value) => {
        return getEnumReadableValue(this.performanceGuaranteeMethod, value);
      },
    },
  ];
}

export interface ISummaryTable {
  label: string;
  valueA: string;
  valueB?: string;
  compare?: boolean;
  formatValue?: () => string;
}
